import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Gear, FloppyDisk, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import RebrandMigrationCard from "../components/RebrandMigrationCard";
import StripeKeysCard from "../components/StripeKeysCard";
import AppCredentials from "../components/integrations/AppCredentials";
import PlatformZapierCard from "../components/PlatformZapierCard";

export default function PlatformSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Subscription defaults
    default_trial_duration_days: 7,
    default_plan: "starter",
    allow_self_signup: true,
    
    // Email defaults
    email_from_name: "Fixlr",
    email_from_email: "support@fixlr.io",
    email_reply_to: "support@fixlr.io",
    
    // System maintenance
    maintenance_mode_enabled: false,
    maintenance_mode_message: "We're performing scheduled maintenance. We'll be back shortly.",
    
    // Platform branding
    platform_name: "Fixlr",
    platform_tagline: "Fix more, Manage less",
    platform_logo_url: "",
    platform_primary_color: "#135DD4",
    platform_secondary_color: "#D6EF37",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await API.get("/platform/settings");
      setSettings(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load platform settings");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/platform/settings", settings);
      toast.success("Platform settings saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] flex items-center gap-2">
            <Gear size={28} className="text-primary" weight="duotone" />
            Platform Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure platform-wide defaults and system settings
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Saving...
            </>
          ) : (
            <>
              <FloppyDisk size={18} />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Subscription Defaults */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold font-[Chivo]">Subscription Defaults</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trial_duration">Trial Duration (Days)</Label>
              <Input
                id="trial_duration"
                type="number"
                value={settings.default_trial_duration_days}
                onChange={(e) => setSettings({ ...settings, default_trial_duration_days: parseInt(e.target.value) })}
                min="0"
                max="90"
              />
              <p className="text-xs text-muted-foreground">Default trial period for new tenants</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="default_plan">Default Plan</Label>
              <select
                id="default_plan"
                value={settings.default_plan}
                onChange={(e) => setSettings({ ...settings, default_plan: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <p className="text-xs text-muted-foreground">Default subscription plan for new tenants</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="space-y-0.5">
              <Label htmlFor="allow_signup">Allow Self-Signup</Label>
              <p className="text-xs text-muted-foreground">Enable public tenant registration</p>
            </div>
            <Switch
              id="allow_signup"
              checked={settings.allow_self_signup}
              onCheckedChange={(checked) => setSettings({ ...settings, allow_self_signup: checked })}
            />
          </div>
        </div>

        {/* Email Defaults */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold font-[Chivo]">Email Defaults</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email_from_name">From Name</Label>
              <Input
                id="email_from_name"
                type="text"
                value={settings.email_from_name}
                onChange={(e) => setSettings({ ...settings, email_from_name: e.target.value })}
                placeholder="Fixlr"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email_from_email">From Email</Label>
              <Input
                id="email_from_email"
                type="email"
                value={settings.email_from_email}
                onChange={(e) => setSettings({ ...settings, email_from_email: e.target.value })}
                placeholder="support@fixlr.io"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email_reply_to">Reply-To Email</Label>
            <Input
              id="email_reply_to"
              type="email"
              value={settings.email_reply_to}
              onChange={(e) => setSettings({ ...settings, email_reply_to: e.target.value })}
              placeholder="support@fixlr.io"
            />
          </div>
        </div>

        {/* System Maintenance */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold font-[Chivo]">System Maintenance</h2>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="maintenance_mode">Maintenance Mode</Label>
              <p className="text-xs text-muted-foreground">Temporarily disable access to the platform</p>
            </div>
            <Switch
              id="maintenance_mode"
              checked={settings.maintenance_mode_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, maintenance_mode_enabled: checked })}
            />
          </div>

          {settings.maintenance_mode_enabled && (
            <div className="space-y-2 pt-2">
              <Label htmlFor="maintenance_message">Maintenance Message</Label>
              <textarea
                id="maintenance_message"
                value={settings.maintenance_mode_message}
                onChange={(e) => setSettings({ ...settings, maintenance_mode_message: e.target.value })}
                className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm"
                placeholder="Enter the message to display to users during maintenance..."
              />
            </div>
          )}
        </div>

        {/* Platform Branding */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold font-[Chivo]">Platform Branding</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="platform_name">Platform Name</Label>
              <Input
                id="platform_name"
                type="text"
                value={settings.platform_name}
                onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
                placeholder="Fixlr"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="platform_tagline">Tagline</Label>
              <Input
                id="platform_tagline"
                type="text"
                value={settings.platform_tagline}
                onChange={(e) => setSettings({ ...settings, platform_tagline: e.target.value })}
                placeholder="Fix more, Manage less"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="platform_logo_url">Logo URL (Optional)</Label>
            <Input
              id="platform_logo_url"
              type="url"
              value={settings.platform_logo_url}
              onChange={(e) => setSettings({ ...settings, platform_logo_url: e.target.value })}
              placeholder="https://example.com/logo.png"
            />
            <p className="text-xs text-muted-foreground">URL to custom logo image</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primary_color"
                  type="color"
                  value={settings.platform_primary_color}
                  onChange={(e) => setSettings({ ...settings, platform_primary_color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  type="text"
                  value={settings.platform_primary_color}
                  onChange={(e) => setSettings({ ...settings, platform_primary_color: e.target.value })}
                  placeholder="#135DD4"
                  className="flex-1"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="secondary_color">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondary_color"
                  type="color"
                  value={settings.platform_secondary_color}
                  onChange={(e) => setSettings({ ...settings, platform_secondary_color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  type="text"
                  value={settings.platform_secondary_color}
                  onChange={(e) => setSettings({ ...settings, platform_secondary_color: e.target.value })}
                  placeholder="#D6EF37"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fixlr rebrand → stored data */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="platform-stripe-card">
          <StripeKeysCard
            endpoint="/platform/settings/stripe"
            variant="platform"
            title="Stripe (platform account)"
            intro="The Stripe account that takes Fixlr subscriptions, one-time licences and supplier tips. Keys saved here override the STRIPE_* deployment environment; switching accounts re-creates the price catalogue on the new account."
          />
        </div>
        <AppCredentials endpoint="/platform/settings/integrations/apps" variant="platform" />
        <PlatformZapierCard />
        <RebrandMigrationCard />
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end pt-4">
        <Button onClick={handleSave} disabled={saving} size="lg" className="gap-2">
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Saving...
            </>
          ) : (
            <>
              <Check size={18} weight="bold" />
              Save All Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
